/**
 * Formats a date as YYYY-MM-DD string (ISO date format).
 * Useful for file naming, API parameters, and date comparisons.
 */
export function formatDateISO(date: Date = new Date()): string {
    return date.toISOString().slice(0, 10);
}

/**
 * Formats a date as relative time (e.g., "2 days ago", "3 hours ago").
 * Returns "Never" if the date is null or undefined.
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
    if (!dateString) {
        return 'Never';
    }

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) {
        return 'Just now';
    } else if (diffMinutes < 60) {
        return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
        return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
        return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    } else if (diffWeeks < 4) {
        return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`;
    } else if (diffMonths < 12) {
        return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
    } else {
        return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
    }
}

/**
 * Formats a date as relative time in the future (e.g., "in 45 minutes").
 * Returns "Expired" if the date is in the past.
 */
/**
 * Converts a Unix timestamp (seconds) to YYYY-MM-DD format.
 */
export function formatTimestampToISO(timestamp: number): string {
    return formatDateISO(new Date(timestamp * 1000));
}

/**
 * Formats a date as relative time in the future (e.g., "in 45 minutes").
 * Returns "Expired" if the date is in the past.
 */
export function formatTimeUntil(dateString: string | null | undefined): string {
    if (!dateString) {
        return 'Unknown';
    }

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs <= 0) {
        return 'Expired';
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) {
        return diffMinutes <= 1 ? 'in 1 minute' : `in ${diffMinutes} minutes`;
    } else if (diffHours < 24) {
        return diffHours === 1 ? 'in 1 hour' : `in ${diffHours} hours`;
    } else {
        return diffDays === 1 ? 'in 1 day' : `in ${diffDays} days`;
    }
}
